import React, { useEffect, useState } from 'react';
import { Toast, ToastHeader, ToastBody, Badge, Row, Col, Card, CardBody, CardHeader, Button } from 'reactstrap';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import './App.css';

const TOKEN_VALIDITY_DURATION = 30000; // 30 secondi

const Slot = ({ slots, selectedSlot, handleSlotSelection, showAlert, associatedID, voucherID, setCurrentSlot, setSlots, setSelectedSlot, handleValidazione, associatedName, fetchSlots }) => {
  const DAYS_PER_PAGE = 3;
  const [token, setToken] = useState(null);
  const [tokenTimestamp, setTokenTimestamp] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const days = Object.keys(slots);
  const totalPages = Math.ceil(days.length / DAYS_PER_PAGE);
  const visibleDays = days.slice(currentPage * DAYS_PER_PAGE, (currentPage + 1) * DAYS_PER_PAGE);

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, Math.max(totalPages - 1, 0)));
  }, [totalPages]);

  const ReCaptchaField = () => {
    useEffect(() => {
      const verify = async () => {
        if (!executeRecaptcha) return;
        const now = Date.now();
        if (token && tokenTimestamp && now - tokenTimestamp < TOKEN_VALIDITY_DURATION) return;
        try {
          const newToken = await executeRecaptcha('prenotazione');
          setToken(newToken);
          setTokenTimestamp(now);
        } catch (error) {
          console.error('Errore reCAPTCHA:', error);
        }
      };
      verify();
      // eslint-disable-next-line
    }, [executeRecaptcha, token, tokenTimestamp]);
    return null;
  };

  useEffect(() => {
    fetchSlots();
    // eslint-disable-next-line
  }, []);

  const handleSubmitAppointment = async () => {
    if (!selectedSlot.giorno || !selectedSlot.ora || !selectedSlot.id_ambulatorio) {
      showAlert('Seleziona un orario');
      return;
    }

    if (!executeRecaptcha) {
      console.error('reCAPTCHA non è pronto');
      showAlert('Errore reCAPTCHA. Riprova più tardi.');
      return;
    }

    showAlert(null);
    setIsSubmitting(true);

    const now = Date.now();
    let tokenToUse = token;
    if (!token || !tokenTimestamp || now - tokenTimestamp >= TOKEN_VALIDITY_DURATION) {
      tokenToUse = await executeRecaptcha('prenotazione');
      setToken(tokenToUse);
      setTokenTimestamp(now);
    }

    try {
      const appuntamento = {
        data: selectedSlot.giorno,
        orario: selectedSlot.ora,
        id_associato: associatedID,
        id_voucher: voucherID,
        tipoVisita: 1,
        note: '',
        id_ambulatorio: selectedSlot.id_ambulatorio
      };

      const response = await fetch(`${process.env.REACT_APP_BASE_SERVER_URI}/visits/selfinsappointment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ appuntamento, 'g-recaptcha-response': tokenToUse }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Errore durante la prenotazione');
      }

      showAlert('Appuntamento prenotato con successo!', 'success');
      sessionStorage.setItem('currentSlotDay', selectedSlot.giorno);
      sessionStorage.setItem('currentSlotHour', selectedSlot.ora);
      sessionStorage.setItem('currentSlotAmb', selectedSlot.id_ambulatorio);
      setSelectedSlot({ giorno: null, ora: null, id_ambulatorio: null });
      setCurrentSlot({ giorno: selectedSlot.giorno, ora: selectedSlot.ora, id_ambulatorio: selectedSlot.id_ambulatorio });
      setToken(null);
      setSlots({});
    } catch (error) {
      showAlert("Riprova più tardi. " + error.message);
      setToken(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card>
      <ReCaptchaField />
      <CardHeader>
        Stai prenotando per {associatedName}. Prossimi orari disponibili
      </CardHeader>
      <CardBody>
        <Row className="my-4 equal-height-row">
          {visibleDays.map((giorno) => (
            <Col key={giorno} xs="12" sm="6" md="4">
              <Toast fade={false} className="toast-fixed-height">
                <ToastHeader className="toast-header-fixed">
                  <span className="toast-hospital-name">Ospedale Mauriziano</span>,{' '}
                  {new Date(giorno).toLocaleDateString('it-IT', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                </ToastHeader>
                <ToastBody className="toast-body-fixed">
                  {slots[giorno].map((slot) => {
                    const isSelected =
                      selectedSlot.giorno === giorno &&
                      selectedSlot.ora === slot.ora &&
                      selectedSlot.id_ambulatorio === slot.id_ambulatorio;

                    return (
                      <Badge
                        key={`${giorno}-${slot.ora}-${slot.id_ambulatorio}`}
                        tag="button"
                        type="button"
                        color={isSelected ? 'primary' : 'success'}
                        className={`m-1 slot-badge${isSelected ? ' slot-badge-selected' : ''}`}
                        aria-pressed={isSelected}
                        onClick={() =>
                          handleSlotSelection(giorno, slot.ora, slot.id_ambulatorio)
                        }
                      >
                        {slot.ora}
                      </Badge>
                    );
                  })}
                </ToastBody>
              </Toast>
            </Col>
          ))}
        </Row>
        {totalPages > 1 && (
          <Row className="justify-content-center align-items-center mb-3">
            <Col xs="auto">
              <Button
                color="secondary"
                outline
                onClick={() => setCurrentPage((page) => page - 1)}
                disabled={currentPage === 0}
                aria-label="Mostra le date precedenti"
              >
                {'<'}
              </Button>
            </Col>
            <Col xs="auto" aria-live="polite">
              Pagina {currentPage + 1} di {totalPages}
            </Col>
            <Col xs="auto">
              <Button
                color="secondary"
                outline
                onClick={() => setCurrentPage((page) => page + 1)}
                disabled={currentPage >= totalPages - 1}
                aria-label="Mostra le date successive"
              >
                {'>'}
              </Button>
            </Col>
          </Row>
        )}
        <Row className="justify-content-center">
          <Col xs="auto">
            <Button color="primary" onClick={handleSubmitAppointment} disabled={isSubmitting}>
              {isSubmitting ? 'Prenotazione in corso...' : 'Conferma appuntamento'}
            </Button>
          </Col>
          <Col xs="auto">
            <Button
              color="secondary"
              className="mb-3"
              onClick={() => {
                showAlert(null);
                handleValidazione(false, null, null, null, null, null, null);
              }}
            >
              {isSubmitting ? 'Prenotazione in corso...' : 'Cambia codice prenotazione'}
            </Button>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};

export default Slot;